// bus.ts — the decoupling layer. Game logic EMITS; it never animates.
//
// This is the most important file in the juice rig — more than any single effect. It is what
// makes "add an effect" mean "subscribe to an existing event" instead of "find the right line in
// the game loop", and it is what makes every effect independently killable (the Vlambeer method).
// There is zero effect code on the game side of this boundary, ever.

/** Every event the game emits, with its payload shape. Add a key here to add a moment. */
export interface Events {
  // The primary "juice" moment: a word cleared the dictionary. The juice stack hangs off this.
  "word:valid": { word: string; tiles: HTMLElement[]; flagged: boolean; at: number };
  // Typed something that isn't a word — the reject.
  "word:invalid": { word: string; tiles: HTMLElement[]; at: number };
  // A valid word that is ALSO on the LDNOOBW flag list. Accepted, scored zero, met with the
  // flattest possible contempt (BRIEF.md tone). Deliberately NOT a block.
  "word:flagged": { word: string; tiles: HTMLElement[]; at: number };
  // Raw submission, pre-validation — for logging / replay bookkeeping.
  "word:submitted": { word: string; at: number };
}

export type EventName = keyof Events;
type Handler<K extends EventName> = (payload: Events[K]) => void;

export class Bus {
  private handlers = new Map<EventName, Set<Handler<any>>>();

  on<K extends EventName>(name: K, fn: Handler<K>): () => void {
    let set = this.handlers.get(name);
    if (!set) this.handlers.set(name, (set = new Set()));
    set.add(fn);
    return () => set!.delete(fn);
  }

  emit<K extends EventName>(name: K, payload: Events[K]): void {
    const set = this.handlers.get(name);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (err) {
        // One misbehaving effect must never break the game or the other effects.
        console.error(`[bus] handler for "${name}" threw:`, err);
      }
    }
  }
}

/** The one shared bus for the app. */
export const bus = new Bus();
